import { apiUrl } from "../../common/api/config";

const authHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: "Bearer " + localStorage.getItem("accessToken"),
});

// --- Alert Templates ---

export const getAlertTemplates = async () => {
  const response = await fetch(`${apiUrl}/alerts/templates/`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error("Failed to fetch alert templates");
  return response.json();
};

export const createAlertTemplate = async (data) => {
  const response = await fetch(`${apiUrl}/alerts/templates/`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!response.ok)
    throw new Error(result.error || "Failed to create template");
  return result;
};

export const updateAlertTemplate = async (id, data) => {
  const response = await fetch(`${apiUrl}/alerts/templates/${id}/`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!response.ok)
    throw new Error(result.error || "Failed to update template");
  return result;
};

export const deleteAlertTemplate = async (id) => {
  const response = await fetch(`${apiUrl}/alerts/templates/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error("Failed to delete template");
};

export const previewAlertTemplate = async (id) => {
  const response = await fetch(`${apiUrl}/alerts/templates/${id}/preview/`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error("Failed to preview template");
  return response.json();
};

// --- Alert Schedules ---

export const getAlertSchedules = async () => {
  const response = await fetch(`${apiUrl}/alerts/schedules/`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error("Failed to fetch schedules");
  return response.json();
};

export const createAlertSchedule = async (data) => {
  const response = await fetch(`${apiUrl}/alerts/schedules/`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!response.ok)
    throw new Error(result.error || "Failed to create schedule");
  return result;
};

export const updateAlertSchedule = async (id, data) => {
  const response = await fetch(`${apiUrl}/alerts/schedules/${id}/`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!response.ok)
    throw new Error(result.error || "Failed to update schedule");
  return result;
};

export const deleteAlertSchedule = async (id) => {
  const response = await fetch(`${apiUrl}/alerts/schedules/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error("Failed to delete schedule");
};

export const triggerAlertSchedule = async (id) => {
  const response = await fetch(`${apiUrl}/alerts/schedules/${id}/trigger/`, {
    method: "POST",
    headers: authHeaders(),
  });
  const result = await response.json();
  if (!response.ok)
    throw new Error(result.error || "Failed to trigger schedule");
  return result;
};

// --- Alert Milestones ---

export const getAlertMilestones = async () => {
  const response = await fetch(`${apiUrl}/alerts/milestones/`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error("Failed to fetch milestones");
  return response.json();
};

export const createAlertMilestone = async (data) => {
  const response = await fetch(`${apiUrl}/alerts/milestones/`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!response.ok)
    throw new Error(result.error || "Failed to create milestone");
  return result;
};

export const updateAlertMilestone = async (id, data) => {
  const response = await fetch(`${apiUrl}/alerts/milestones/${id}/`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!response.ok)
    throw new Error(result.error || "Failed to update milestone");
  return result;
};

export const deleteAlertMilestone = async (id) => {
  const response = await fetch(`${apiUrl}/alerts/milestones/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error("Failed to delete milestone");
};

// --- Alert Logs ---

export const getAlertLogs = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const response = await fetch(
    `${apiUrl}/alerts/logs/${query ? "?" + query : ""}`,
    { method: "GET", headers: authHeaders() },
  );
  if (!response.ok) throw new Error("Failed to fetch alert logs");
  return response.json();
};

// --- SMS Subscription ---

export const getSMSStatus = async () => {
  const response = await fetch(`${apiUrl}/alerts/sms/status/`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error("Failed to fetch SMS status");
  return response.json();
};

export const subscribeSMS = async (tier) => {
  const response = await fetch(`${apiUrl}/alerts/sms/subscribe/`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ tier }),
  });
  const result = await response.json();
  if (!response.ok)
    throw new Error(result.error || "Failed to subscribe to SMS");
  return result;
};

export const cancelSMS = async () => {
  const response = await fetch(`${apiUrl}/alerts/sms/cancel/`, {
    method: "POST",
    headers: authHeaders(),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || "Failed to cancel SMS");
  return result;
};

export const verifyPhone = async (phoneNumber) => {
  const response = await fetch(`${apiUrl}/alerts/sms/verify-phone/`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ phone_number: phoneNumber }),
  });
  const result = await response.json();
  if (!response.ok)
    throw new Error(result.error || "Failed to send verification");
  return result;
};

export const confirmPhone = async (phoneNumber, code) => {
  const response = await fetch(`${apiUrl}/alerts/sms/confirm-phone/`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ phone_number: phoneNumber, code }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || "Failed to confirm phone");
  return result;
};

// --- Shared Report Links ---

export const getSharedLinks = async () => {
  const response = await fetch(`${apiUrl}/alerts/share-report/`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error("Failed to fetch shared links");
  return response.json();
};

export const createSharedLink = async (data) => {
  const response = await fetch(`${apiUrl}/alerts/share-report/`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  const result = await response.json();
  if (!response.ok)
    throw new Error(result.error || "Failed to create shared link");
  return result;
};

export const revokeSharedLink = async (id) => {
  const response = await fetch(`${apiUrl}/alerts/share-report/${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!response.ok) throw new Error("Failed to revoke shared link");
};

// --- Public Shared Report ---

export const getSharedReport = async (token) => {
  const response = await fetch(`${apiUrl}/alerts/shared/${token}/`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error("Report not found or expired");
  return response.json();
};

export const verifySharedReportPassword = async (token, password) => {
  const response = await fetch(`${apiUrl}/alerts/shared/${token}/verify/`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ password }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || "Verification failed");
  return result;
};
